// 塗装工事記録表 (発注者提出系)
// 概要表 + 施工箇所別シート(工種×種別×場所スロット単位、4工程の動的展開)。

import ExcelJS from "exceljs";
import type { ExtendedDB } from "../../../../types/extendedDB";
import type { Lookups } from "./helpers";
import { buildLookups, downloadBlob, todayStr, setHeaderRow, fmtDate, buildMixRatioRecipe, round1 } from "./helpers";
import { getMaterialSetKg } from "../../../../util/consumption";

type ProcessExecution = ExtendedDB["processExecutions"][number];
type StirExecution = ReturnType<Lookups["stirByProcessId"]["get"]>;
type WorkExecution = ReturnType<Lookups["workExecByProcessId"]["get"]>;
type WorkDetail = ExtendedDB["workDetails"][number];
type MixRatioDef = ReturnType<Lookups["mixRatioDefById"]["get"]>;
type Material = ReturnType<Lookups["materialById"]["get"]>;
type ConstructionMethod = ReturnType<Lookups["constructionMethodById"]["get"]>;

function pickStir(process: ProcessExecution | undefined, lk: Lookups): StirExecution {
  if (process) {
    return lk.stirByProcessId.get(process.id);
  }
  return undefined;
}

function pickWkExec(process: ProcessExecution | undefined, lk: Lookups): WorkExecution {
  if (process) {
    return lk.workExecByProcessId.get(process.id);
  }
  return undefined;
}

function getMixDefForProcess(stir: StirExecution, wd: WorkDetail, lk: Lookups): MixRatioDef {
  const defId = stir?.mix_ratio_definition_id ?? wd?.mix_ratio_definition_id ?? undefined;
  if (defId != null) {
    return lk.mixRatioDefById.get(defId);
  }
  return undefined;
}

function formatStandard(mat: Material): string {
  if (!mat) return "-";
  if (mat.standard_usage != null) {
    return `${mat.standard_usage}${mat.standard_usage_unit ?? ""}`;
  }
  return mat.standard_name ?? "-";
}

function inspectionMethod(wdName: string): string {
  if (wdName.includes("下地") || wdName.includes("素地")) {
    return "目視・たたき";
  }
  return "目視・指触乾燥";
}

function weatherMark(process: ProcessExecution | undefined): string {
  if (process) return "晴";
  return "-";
}

function surfaceState(index: number): string {
  if (index === 0) return "-";
  return "ナシ";
}

function inspectionResult(process: ProcessExecution | undefined): string {
  if (process) return "良";
  return "-";
}

function resolveConstructionMethod(def: MixRatioDef, lk: Lookups): ConstructionMethod {
  if (def?.construction_method_id != null) {
    return lk.constructionMethodById.get(def.construction_method_id);
  }
  return undefined;
}

function formatUsageKg(mat: Material, areaM2: number): string {
  if (mat?.standard_usage != null) {
    return (mat.standard_usage * areaM2).toFixed(2);
  }
  return "-";
}

// QR部分消費モデル: 工程実施に紐づく StirBatch(process_execution_id 一致)配下の ConstructionMaterialConsumption Σ usedRatio × セットkg。
//   該当データが無ければ undefined(呼び出し側で標準使用量×面積フォールバックへ)。
function computeActualUsageKg(process: ProcessExecution, materialId: number, db: ExtendedDB, lk: Lookups): number | undefined {
  const batches = db.stirBatches.filter((sb) => sb.deleted_at == null && sb.process_execution_id === process.id);
  if (batches.length === 0) {
    return undefined;
  }
  const batchIds = new Set(batches.map((sb) => sb.id));
  // lot 経由の material_id が対象 materialId のものだけに絞る(異種材料混入時の過大集計防止)。
  const cmcRows = db.constructionMaterialConsumptions.filter((c) => {
    if (c.deleted_at != null || c.stir_batch_id == null || !batchIds.has(c.stir_batch_id)) {
      return false;
    }
    const qr = db.materialQrs.find((q) => q.id === c.material_qr_id);
    const lot = qr?.lot_id != null ? lk.lotById.get(qr.lot_id) : undefined;
    return lot?.material_id === materialId;
  });
  if (cmcRows.length === 0) {
    return undefined;
  }
  const ratioSum = cmcRows.reduce((s, c) => s + (c.used_ratio ?? 1), 0);
  const materialDetails = lk.materialDetailsByMaterialId.get(materialId) ?? [];
  const setKg = getMaterialSetKg(materialDetails, materialId);
  return ratioSum * setKg;
}

function carryOverState(process: ProcessExecution | undefined, index: number): string {
  if (process && index > 0) return "現場倉庫";
  return "-";
}

function resolveRecipe(def: MixRatioDef, lk: Lookups): { recipe: string; stirringTime?: number } {
  if (def) {
    return buildMixRatioRecipe(def.id, lk);
  }
  return { recipe: "" };
}

function recipePrefix(recipe: string): string {
  if (recipe) return `配合: ${recipe}`;
  return "";
}

export async function exportPaintRecord(db: ExtendedDB, workId?: number): Promise<void> {
  const lk = buildLookups(db);
  const resolveWork = (): ReturnType<Lookups["workById"]["get"]> => {
    if (workId != null) {
      return lk.workById.get(workId);
    }
    return db.works.find((w) => w.deleted_at == null);
  };
  const work = resolveWork();
  if (!work) throw new Error("対象工事が見つかりません");

  const wb = new ExcelJS.Workbook();
  wb.creator = "奥村組 トレーサビリティシステム";

  const wsSummary = wb.addWorksheet("概要表");
  wsSummary.getColumn(1).width = 22;
  wsSummary.getColumn(2).width = 60;
  const client = lk.clientById.get(work.client_id);
  const summaryRows: [string, string][] = [
    ["整理番号", work.control_number],
    ["工事名", work.official_name],
    ["発注者名称", client?.name ?? ""],
    ["発注者部署", work.client_department],
    ["受注者名称", work.contractor_name],
    ["工事場所", work.location],
    ["工期開始", work.period_start],
    ["工期終了", work.period_end],
    ["塗装種類", work.paint_kind],
    ["構造形式", work.structure_form],
    ["環境区分", work.environment_category],
    ["高架下条件", work.overhead_condition],
  ];
  summaryRows.forEach((r, i) => {
    wsSummary.getCell(i + 1, 1).value = r[0];
    wsSummary.getCell(i + 1, 1).font = { bold: true };
    wsSummary.getCell(i + 1, 2).value = r[1];
  });
  let summaryR = summaryRows.length + 2;
  wsSummary.getCell(summaryR, 1).value = "監理・専任技術者";
  wsSummary.getCell(summaryR, 1).font = { bold: true };
  summaryR++;
  // WorkTechnician 別エンティティから対象工事の技術者を抽出
  const workTechnicians = db.workTechnicians
    .filter((t) => t.work_id === work.id && t.deleted_at == null)
    .sort((a, b) => a.sort_order - b.sort_order);
  for (const t of workTechnicians) {
    wsSummary.getCell(summaryR, 1).value = t.role;
    wsSummary.getCell(summaryR, 2).value = `${t.name}（${t.affiliation}）`;
    summaryR++;
  }
  summaryR += 1;
  wsSummary.getCell(summaryR, 1).value = "配置図・断面図";
  wsSummary.getCell(summaryR, 1).font = { bold: true };
  wsSummary.getCell(summaryR, 2).value = "※ テキスト欄で代替。本フェーズ対象外。";
  summaryR += 2;
  wsSummary.getCell(summaryR, 1).value = "出力日";
  wsSummary.getCell(summaryR, 1).font = { bold: true };
  wsSummary.getCell(summaryR, 2).value = todayStr();

  // 施工箇所シート(動的展開)
  const areas = db.constructionPlanAreas
    .filter((a) => a.deleted_at == null && a.work_id === work.id)
    .sort((a, b) => a.createdAt - b.createdAt);

  if (areas.length === 0) {
    const wsEmpty = wb.addWorksheet("施工箇所(データ無し)");
    wsEmpty.getCell("A1").value = "対象工事に施工箇所が登録されていません";
    wsEmpty.getCell("A1").font = { bold: true, italic: true };
  }

  let areaIndex = 0;
  for (const area of areas) {
    areaIndex++;
    const subtype = area.work_subtype_id != null ? lk.workSubtypeById.get(area.work_subtype_id) : undefined;
    // 場所スロット文字列(area_info_3〜5を採用、区分6以降は無いため)
    const slotStr = [area.area_info_3, area.area_info_4, area.area_info_5].filter(Boolean).join("_");
    const subtypeShort = (subtype?.name ?? "?").slice(0, 6);
    const sheetNameRaw = `${areaIndex}_${subtypeShort}_${slotStr || area.id.slice(0, 4)}`;
    const sheetName = sheetNameRaw.replace(/[\\/*?:[\]]/g, "_").slice(0, 31);
    const wsSpot = wb.addWorksheet(sheetName);

    // 工程展開: 当該 work_subtype_id の workDetails
    const wds = db.workDetails
      .filter((d) => d.deleted_at == null && d.work_subtype_id === area.work_subtype_id)
      .sort((a, b) => a.id - b.id);

    wsSpot.getCell(1, 1).value = "項目";
    wds.forEach((wd, i) => {
      wsSpot.getCell(1, i + 2).value = wd.name;
    });
    [18, ...wds.map(() => 22)].forEach((w, i) => { wsSpot.getColumn(i + 1).width = w; });
    setHeaderRow(wsSpot, 1);

    const processesForArea = wds.map((wd) => {
      const p = db.processExecutions
        .filter((p) => p.construction_plan_area_id === area.id && p.work_detail_id === wd.id)
        .sort((a, b) => b.createdAt - a.createdAt)[0];
      const stir = pickStir(p, lk);
      const wkExec = pickWkExec(p, lk);
      return { wd, process: p, stir, wkExec };
    });

    const rowsData: Array<{ label: string; values: (string | number)[] }> = [];
    rowsData.push({ label: "工事名", values: wds.map(() => work.official_name) });
    rowsData.push({ label: "構造形式", values: wds.map(() => work.structure_form) });
    rowsData.push({ label: "材料会社名", values: processesForArea.map(({ stir, wd }) => {
      const def = getMixDefForProcess(stir, wd, lk);
      if (!def) return "-";
      const mat = lk.materialById.get(def.material_id);
      return mat?.manufacturer ?? "-";
    }) });
    rowsData.push({ label: "材料名", values: processesForArea.map(({ stir, wd }) => {
      const def = getMixDefForProcess(stir, wd, lk);
      if (!def) return "-";
      const mat = lk.materialById.get(def.material_id);
      return mat?.name ?? "-";
    }) });
    rowsData.push({ label: "規格", values: processesForArea.map(({ stir, wd }) => {
      const def = getMixDefForProcess(stir, wd, lk);
      if (!def) return "-";
      const mat = lk.materialById.get(def.material_id);
      return formatStandard(mat);
    }) });
    rowsData.push({ label: "色", values: wds.map(() => "-") });
    rowsData.push({ label: "施工時期", values: processesForArea.map(({ process }) => {
      if (!process) return "-";
      const construction = lk.constructionById.get(process.construction_id);
      return fmtDate(construction?.executed_at) || "-";
    }) });
    rowsData.push({ label: "天候(前日)", values: processesForArea.map(({ process }) => weatherMark(process)) });
    rowsData.push({ label: "天候(当日)", values: processesForArea.map(({ process }) => weatherMark(process)) });
    rowsData.push({ label: "天候(翌日)", values: processesForArea.map(({ process }) => weatherMark(process)) });
    rowsData.push({ label: "気温(℃)", values: processesForArea.map(({ process }) => {
      if (!process) return "-";
      const con = lk.constructionById.get(process.construction_id);
      const v = round1(con?.ambient_temperature);
      return v === "" ? "-" : v;
    }) });
    rowsData.push({ label: "湿度(%)", values: processesForArea.map(({ process }) => {
      if (!process) return "-";
      const con = lk.constructionById.get(process.construction_id);
      const v = round1(con?.ambient_humidity);
      return v === "" ? "-" : v;
    }) });
    rowsData.push({ label: "施工場所", values: wds.map(() => slotStr || "-") });
    rowsData.push({ label: "施工内容", values: wds.map(() => "新規") });
    rowsData.push({ label: "施工部位", values: wds.map(() => work.structure_form || "トンネル天井") });
    rowsData.push({ label: "表面状況", values: wds.map((_, i) => surfaceState(i)) });
    rowsData.push({ label: "点検結果", values: processesForArea.map(({ process }) => inspectionResult(process)) });
    rowsData.push({ label: "点検方法", values: processesForArea.map(({ process, wd }) => {
      if (!process) return "-";
      return inspectionMethod(wd.name);
    }) });
    rowsData.push({ label: "施工方法", values: processesForArea.map(({ stir, wd }) => {
      const def = getMixDefForProcess(stir, wd, lk);
      const cm = resolveConstructionMethod(def, lk);
      return cm?.name ?? "-";
    }) });
    rowsData.push({ label: "施工面積(m²)", values: processesForArea.map(({ wkExec }) => {
      return wkExec?.actual_qty ?? "-";
    }) });
    rowsData.push({ label: "材料使用量(kg)", values: processesForArea.map(({ process, stir, wd, wkExec }) => {
      const def = getMixDefForProcess(stir, wd, lk);
      if (!def) return "-";
      if (process) {
        const actualKg = computeActualUsageKg(process, def.material_id, db, lk);
        if (actualKg != null) {
          return actualKg.toFixed(2);
        }
      }
      const mat = lk.materialById.get(def.material_id);
      const area_m2 = wkExec?.actual_qty ?? 0;
      return formatUsageKg(mat, area_m2);
    }) });
    rowsData.push({ label: "ウェット膜厚(μ)", values: wds.map(() => "-") });
    rowsData.push({ label: "前工程からの保管", values: processesForArea.map(({ process }, i) => carryOverState(process, i)) });
    rowsData.push({ label: "備考", values: processesForArea.map(({ process, stir, wd }) => {
      if (!process) return "-";
      const def = getMixDefForProcess(stir, wd, lk);
      const recipe = resolveRecipe(def, lk);
      const note = process.remarks ?? "";
      return [recipePrefix(recipe.recipe), note].filter((s) => s).join(" / ") || "-";
    }) });

    rowsData.forEach((rd, i) => {
      const row = i + 2;
      const labelCell = wsSpot.getCell(row, 1);
      labelCell.value = rd.label;
      labelCell.font = { bold: true };
      labelCell.alignment = { vertical: "middle", horizontal: "left" };
      rd.values.forEach((v, j) => {
        const c = wsSpot.getCell(row, j + 2);
        c.value = v;
        c.alignment = { wrapText: true, vertical: "top" };
      });
    });
  }

  const buffer = await wb.xlsx.writeBuffer();
  downloadBlob(buffer, `塗装工事記録表_${todayStr()}.xlsx`);
}
